import { nekoPos } from 'public-contracts';
import { configs } from '../configs';
import authProvider from '../authProvider';

type Campaign = nekoPos.Campaign;
type CampaignEntry = nekoPos.CampaignEntry;

const getAuthHeader = async () => {
  const auth = authProvider.auth;
  const token = await auth.getAccessToken();
  return token ? `Bearer ${token}` : '';
};

const apiCall = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = await getAuthHeader();
  const response = await fetch(`${configs.apiRoot}/campaigns${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export default class CampaignService {
  async getCampaigns(): Promise<Campaign[]> {
    try {
      return await apiCall<Campaign[]>('');
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  }

  async getCampaign(id: string): Promise<Campaign> {
    try {
      return await apiCall<Campaign>(`/${id}`);
    } catch (error) {
      console.error('Error fetching campaign:', error);
      throw error;
    }
  }

  async createCampaign(campaignData: CampaignEntry): Promise<Campaign> {
    try {
      return await apiCall<Campaign>('', {
        method: 'POST',
        body: JSON.stringify(campaignData),
      });
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  async updateCampaign(
    id: string,
    campaignData: Partial<CampaignEntry>
  ): Promise<Campaign> {
    try {
      return await apiCall<Campaign>(`/${id}`, {
        method: 'PUT',
        body: JSON.stringify(campaignData),
      });
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  async deleteCampaign(id: string): Promise<void> {
    try {
      await apiCall(`/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  }

  async getCampaignReport(campaignId: string): Promise<any> {
    try {
      const token = await getAuthHeader();
      const response = await fetch(
        `${configs.apiRoot}/reports/campaign/${campaignId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Campaign report API response:', data);
      console.log('Response structure:', {
        hasData: !!data,
        hasDataProperty: !!data?.data,
        keys: Object.keys(data || {}),
        campaign: !!data?.campaign || !!data?.data?.campaign,
        metrics: !!data?.metrics || !!data?.data?.metrics,
      });
      
      return data;
    } catch (error) {
      console.error('Error fetching campaign report:', error);
      throw error;
    }
  }
}

export const campaignServiceInstance = new CampaignService();

